import {
  useEffect,
  useState,
  type FormEvent,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Church,
  KeyRound,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";

import { AuthLayout } from "@/components/auth/AuthLayout";

import { forgotPassword } from "../api/authApi";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] =
    useState(false);
  const [isSubmitted, setIsSubmitted] =
    useState(false);
  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  useEffect(() => {
    setErrorMessage(null);
  }, [email]);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    const normalizedEmail =
      email.trim().toLowerCase();

    if (!normalizedEmail) {
      setErrorMessage(
        "Enter the email address associated with your workspace account.",
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await forgotPassword({
        email: normalizedEmail,
      });

      setIsSubmitted(true);
    } catch {
      setErrorMessage(
        "MinistryIQ could not process your request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      variant="platform"
      workspaceLabel="Church Workspace"
      brandIcon={Church}
      cardIcon={KeyRound}
      eyebrow="Account Recovery"
      heroTitle="Return to the people who depend on you."
      heroDescription="Request a secure password reset link and regain access to your MinistryIQ church workspace."
      formTitle={
        isSubmitted
          ? "Check your email"
          : "Reset your password"
      }
      formDescription={
        isSubmitted
          ? "If an active MinistryIQ account exists for that address, a secure reset link has been sent."
          : "Enter the email address associated with your MinistryIQ account."
      }
      footer="Password reset links expire after 30 minutes and can only be used once."
    >
      {isSubmitted ? (
        <div className="mt-8">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <Mail className="h-5 w-5" />
              </div>

              <div>
                <p className="font-semibold text-emerald-900">
                  Reset instructions requested
                </p>

                <p className="mt-2 text-sm leading-6 text-emerald-800">
                  Check your inbox and spam folder for an
                  email from MinistryIQ. For security, we
                  show this same confirmation whether or not
                  an account exists.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <Link
              to="/login"
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-500"
            >
              Return to sign in
              <ArrowRight className="h-4 w-4" />
            </Link>

            <button
              type="button"
              onClick={() => {
                setIsSubmitted(false);
                setErrorMessage(null);
              }}
              className="inline-flex h-11 w-full items-center justify-center text-sm font-semibold text-slate-500 transition hover:text-slate-800"
            >
              Try another email address
            </button>
          </div>
        </div>
      ) : (
        <>
          {errorMessage && (
            <div
              role="alert"
              className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
            >
              <p className="text-sm font-medium text-rose-700">
                {errorMessage}
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-5"
          >
            <div>
              <label
                htmlFor="reset-email"
                className="text-sm font-semibold text-slate-700"
              >
                Email address
              </label>

              <div className="relative mt-2">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />

                <input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-12 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Sending instructions...
                </>
              ) : (
                <>
                  Send reset instructions
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center gap-2 pt-1 text-sm font-semibold text-slate-500 transition hover:text-indigo-600"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
